import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Dispatch, connect } from "react-redux";
import { Row, Button, FormControl, FormGroup, Col, Jumbotron, ToggleButtonGroup, ToggleButton, Well } from 'react-bootstrap';
import MediaQuery from 'react-responsive';
import Loader from 'react-spinners/ClockLoader';
import { IWatrTransactionsResponse } from '@nkr/store/watr/actions';
import { IWatrReimbursement } from '@nkr/models/index';
import { IApplicationState, AppActionCreators, IAppState, WatrActionCreators } from '@nkr/store/index';
import { ColumnApi, GridApi, GridReadyEvent, FirstDataRenderedEvent, GridSizeChangedEvent } from 'ag-grid-community';
import TransactionGrid from './transaction-grid';
import { ReimbursementType } from '@nkr/enums/reimbursement-type';
import { OrganType } from '@nkr/enums/organ-type';
import { downloadFile } from '@nkr/services/download-file';

interface IProps {
    gridApi: GridApi,
    columnApi: ColumnApi,
    onGridReady: (params: GridReadyEvent) => void,
    organTypes: OrganType[],
    onChangeOrganType: (organTypes: OrganType[]) => void,
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getTransactions: (startDate, endDate, getCsv, organType?: OrganType) => Promise<IWatrTransactionsResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    [index: string]: IWatrReimbursement[] | string | boolean
    list: IWatrReimbursement[],
    startDate: string,
    endDate: string,
    fetching: boolean,
    hasComplicationsOnly: boolean
}

class Transactions extends React.Component<TProps, IState> {
    readonly state: IState = {
        list: [],
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
        fetching: false,
        hasComplicationsOnly: false
    }

    container: HTMLDivElement;
    intervalId: any;

    onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // get the current grids width
        let grid = document.getElementById('grid-wrapper');

        if (grid == null)
            return;

        let gridWidth = grid.offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;

        let allColumns = e.columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];

            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        e.api.sizeColumnsToFit();
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData(true, false);
    }

    componentDidUpdate = (prevProps: TProps) => {
        if (!_.isEqual(prevProps.organTypes, this.props.organTypes)) {
            this.getData(true, false);
        }
    }

    handleComplicationsOnlyFilter = () => {

        this.setState({
            hasComplicationsOnly: !this.state.hasComplicationsOnly
        });       
    }

    getFilteredTransactionsList = () => {
        let filteredList = [];
        
        if (this.state.hasComplicationsOnly) {
            filteredList = this.state.list.filter(x => {
                if (x.reimbursementType == ReimbursementType.TravelAndLodgingForComplication
                    || x.reimbursementType == ReimbursementType.WagesForComplication)
                    return x;
            });
        } else {
            filteredList = this.state.list;
        }

        return filteredList;
    }

    getData = (message: boolean, getCsv: boolean) => {
        let start = message ? this.props.showLoading('Getting registrations, one moment please...') : Promise.resolve(null);
        let organTypes = this.props.organTypes || [];
        let organType = organTypes.length == 1 ? organTypes[0] : undefined;

        start.then(() => {
            this.setState({
                fetching: message == true ? false : true
            }, () => {

                this.props.getTransactions(this.state.startDate, this.state.endDate, getCsv, organType)
                    .then(resp => {

                        if (getCsv) {
                            var fileName = `watr-transactions-` + moment(this.state.startDate).format('MM/DD/YYYY') + `-` + moment(this.state.endDate).format('MM/DD/YYYY') + `.csv`;
                            downloadFile(resp.data, fileName, 'text/csv');
                        } else {
                            if (resp.data.success) {
                                this.setState({
                                    list: resp.data.results,
                                    fetching: false
                                }, () => {
                                    if (this.props.gridApi != undefined) {
                                        this.props.gridApi.refreshCells();
                                    }
                                });
                            }
                        }

                        this.props.hideLoading();
                    });
            });
        });
    }

    handleInputChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        this.setState({
            [name]: value
        });
    }

    handleSearch = (getCsv: boolean) => {
        this.getData(true, getCsv);
    }

    render = () => {
        let transactionsList = this.getFilteredTransactionsList();

        return (
            <>
                <div style={{ display: this.state.fetching ? 'flex' : 'none', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ display: 'flex' }}>
                            {this.state.fetching && (
                                <>
                                    <div style={{ marginRight: 5 }}>Fetching Watr Requests...</div>
                                    <Loader
                                        size={20}
                                        color={'#a1a1a1'}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <MediaQuery minWidth={1023} style={{ padding: 0 }}>
                    <Well bsSize='sm' style={{ marginBottom: 5 }}>
                        <Row>
                            <Col sm={12}>
                                <Col sm={2}>
                                    <FormGroup>
                                        <label className='control-label'>Start Date</label>
                                        <FormControl
                                            className='input-group'
                                            type='date'
                                            id='date'
                                            name='startDate'
                                            value={this.state.startDate}
                                            onChange={this.handleInputChange}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col sm={2}>
                                    <FormGroup>
                                        <label className='control-label'>End Date</label>
                                        <FormControl
                                            className='input-group'
                                            type='date'
                                            id='date'
                                            name='endDate'
                                            value={this.state.endDate}
                                            onChange={this.handleInputChange}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col sm={3}>
                                    <FormGroup>
                                        <label className='control-label'>Organ</label>
                                        <div>
                                            <ToggleButtonGroup
                                                type='radio'
                                                name='organs'
                                                value={this.props.organTypes.length == 2 ? -1 : this.props.organTypes[0]}
                                            >
                                                <ToggleButton
                                                    value={-1}
                                                    onChange={() => this.props.onChangeOrganType([OrganType.Kidney, OrganType.Liver])}
                                                    className={this.props.organTypes.length == 2 ? 'btn-primary btn-md' : 'btn-md'}
                                                >
                                                    All
                                                </ToggleButton>
                                                <ToggleButton
                                                    value={OrganType.Kidney}
                                                    onChange={() => this.props.onChangeOrganType([OrganType.Kidney])}
                                                    className={this.props.organTypes.length == 1 && this.props.organTypes.includes(OrganType.Kidney) ? 'btn-primary btn-md' : 'btn-md'}
                                                >
                                                    Kidney
                                                </ToggleButton>
                                                <ToggleButton
                                                    value={OrganType.Liver}
                                                    onChange={() => this.props.onChangeOrganType([OrganType.Liver])}
                                                    className={this.props.organTypes.length == 1 && this.props.organTypes.includes(OrganType.Liver) ? 'btn-primary btn-md' : 'btn-md'}
                                                >
                                                    Liver
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </div>
                                    </FormGroup>
                                </Col>

                                <Col sm={2}>
                                    <FormGroup>
                                        <label className='control-label'></label>
                                            <Button
                                                value=""
                                                className={this.state.hasComplicationsOnly == true ? "btn-primary" : ""}
                                                onClick={() => this.handleComplicationsOnlyFilter()}
                                                style={{ marginTop: '25px' }}>
                                                Complications Only
                                            </Button>
                                    </FormGroup>
                                </Col>

                                <Col sm={1}>
                                    <label className='control-label'></label>
                                    <Button bsStyle="primary"
                                        onClick={() => this.handleSearch(false)}
                                        className='input-group'
                                        style={{ marginTop: '5px' }}
                                    >
                                        Search
                                    </Button>
                                </Col>

                                <Col sm={2}>
                                    <label className='control-label'></label>
                                    <Button bsStyle="warning"
                                        onClick={() => this.handleSearch(true)}
                                        className='input-group'
                                        style={{ marginTop: '5px' }}
                                    >
                                        Export Report
                                    </Button>
                                </Col>
                            </Col>
                        </Row>
                    </Well>

                    <TransactionGrid
                        data={transactionsList}
                        onGridReady={this.props.onGridReady}
                        onFirstDataRendered={this.onFirstDataRendered}
                        onGridSizeChanged={this.onGridSizeChanged}
                    />
                </MediaQuery>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getTransactions: (startDate, endDate, getCsv, organType) => {
            return dispatch(WatrActionCreators.getTransactions(startDate, endDate, getCsv, organType));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(Transactions);